import * as React from 'react'
import Button from '@doordash/component-button'
import Form from '../../../utilities/Form'
import s from './strings'
import styled from 'styled-components'
import { noop } from 'lodash'
import { Select, TextField } from '@doordash/component-fields'
import { useState } from 'react'
import { validators as sharedValidators } from '../../../utilities/validators'
import {
  Colors,
  InlineChildren,
  StackChildren,
  Text,
} from '@doordash/design-language'
import {
  AUSTRALIAN_STATES,
  countriesForDropDown,
} from '../../../utilities/countries'

const DurationWrapper = styled(InlineChildren)`
  display: flex;
`

export type IAddress = {
  id: number
  country: string
  streetAddress: string
  months: number
  years: number
  state: string
  city: string
  zipCode: string
  valid: boolean
  currentAddress: boolean
}

interface IResidentialAddressProps {
  address: IAddress
  updateAddress: (id: number, data: IAddress) => void
  removeAddress?: (id: number, years: string, months: string) => void
  updateYears?: (prevYears: string, newYears: string) => void
  updateMonths?: (prevMonths: string, newMonths: string) => void
  /**
   * isCurrentAddress: boolean flag passed to determing if its the users'
   * current residential address
   */
  isCurrentAddress?: boolean
  /**
   * array of refs for storing references to all the address forms,
   * this is used to submit all the forms from parent component.
   */
  refs?: React.RefObject<HTMLElement[]>
}

export const AUSTRALIA_COUNTRY_CODE = 'AUS'

const YEAR_VALUES = [
  {
    name: '0',
    value: '0',
  },
  {
    name: '1',
    value: '1',
  },
  {
    name: '2',
    value: '2',
  },
  {
    name: '3',
    value: '3',
  },
  {
    name: '4',
    value: '4',
  },
  {
    name: '5+',
    value: '5',
  },
]

export const MONTH_VALUES = [
  {
    name: '0',
    value: '0',
  },
  {
    name: '1',
    value: '1',
  },
  {
    name: '2',
    value: '2',
  },
  {
    name: '3',
    value: '3',
  },
  {
    name: '4',
    value: '4',
  },
  {
    name: '5',
    value: '5',
  },
  {
    name: '6',
    value: '6',
  },
  {
    name: '7',
    value: '7',
  },
  {
    name: '8',
    value: '8',
  },
  {
    name: '9',
    value: '9',
  },
  {
    name: '10',
    value: '10',
  },
  {
    name: '11',
    value: '11',
  },
]

export const ResidentialAddress = ({
  address,
  removeAddress,
  updateAddress,
  updateYears,
  updateMonths,
  isCurrentAddress = false,
  refs,
}: IResidentialAddressProps) => {
  const [country, setCountry] = useState<string>(address.country)

  const fields = {
    country: address.country,
    streetAddress: address.streetAddress,
    months: address.months,
    years: address.years,
    state: address.state,
    city: address.city,
    zipCode: address.zipCode,
  }

  const isCountryAU = country === AUSTRALIA_COUNTRY_CODE

  const validators = {
    country: sharedValidators.address.country,
    streetAddress: sharedValidators.address.street,
    state: sharedValidators.address.state,
    city: sharedValidators.address.city,
    zipCode: sharedValidators.address.zipCode,
    years: noop,
    months: noop,
  }

  const submit = async data => {
    updateAddress(address.id, data)
  }

  return (
    <Form onSubmit={submit} validators={validators} fields={fields}>
      {(fields, handleChange, handleSubmit) => (
        <form
          onSubmit={handleSubmit}
          ref={el => (refs.current[address.id] = el)}
        >
          <StackChildren>
            <Text styles={Text.Styles.Label2Emphasis}>
              {isCurrentAddress
                ? s.currentResidentialAddress()
                : s.previousResidentialAddress()}
            </Text>
            <Select
              description={s.required()}
              label={s.country()}
              isDisabled={isCurrentAddress}
              onChange={value => {
                setCountry(value)
                handleChange('country', value)
                handleChange('streetAddress', '')
                handleChange(
                  'state',
                  value === AUSTRALIA_COUNTRY_CODE ? 'ACT' : ''
                )
                handleChange('city', '')
                handleChange('zipCode', '')
              }}
              options={countriesForDropDown}
              {...fields.country}
            />
            <TextField
              description={s.required()}
              placeholder={s.streetAddress()}
              onChange={value => {
                handleChange('streetAddress', value)
              }}
              label={s.streetAddress()}
              {...fields.streetAddress}
            />
            <TextField
              description={s.required()}
              placeholder={s.city()}
              onChange={value => {
                handleChange('city', value)
              }}
              value={fields.city.value}
              label={s.city()}
              {...fields.city}
            />
            {
              isCountryAU && (
                <Select
                  description={s.required()}
                  label={s.state()}
                  onChange={value => {
                    handleChange('state', value)
                  }}
                  options={AUSTRALIAN_STATES}
                  {...fields.state}
                />
              )
            }
            {
              !isCountryAU && (
                <TextField
                  description={s.required()}
                  placeholder={s.state()}
                  onChange={value => {
                    handleChange('state', value)
                  }}
                  value={fields.state.value}
                  label={s.state()}
                  {...fields.state}
                />
              )
            }
            <TextField
              description={s.required()}
              type="zip"
              placeholder={s.postCode()}
              onChange={value => {
                handleChange('zipCode', value)
              }}
              label={s.postCode()}
              {...fields.zipCode}
            />

            <Text styles={Text.Styles.Label2Emphasis}>
              {s.durationAtAddressText()}
            </Text>
            <Text
              styles={Text.Styles.Caption1}
              color={Colors.ButtonFlatSecondaryForegroundPressed}
            >
              {s.dontIncludeThisMonthText()}
            </Text>
            <DurationWrapper>
              <Select
                label={s.years()}
                description={s.required()}
                placeholder="0"
                onChange={value => {
                  handleChange('years', parseInt(value, 10))
                  updateYears(fields.years.value, value)
                }}
                options={YEAR_VALUES}
                {...fields.years}
                value={fields.years.value.toString()}
              />
              <Select
                label={s.months()}
                description={s.required()}
                placeholder="0"
                isRequired={true}
                onChange={value => {
                  handleChange('months', parseInt(value, 10))
                  updateMonths(fields.months.value, value)
                }}
                options={MONTH_VALUES}
                {...fields.months}
                value={fields.months.value.toString()}
              />
            </DurationWrapper>
            {!isCurrentAddress && (
              <InlineChildren justifyContent="flex-end">
                <Button
                  onClick={() =>
                    removeAddress(
                      address.id,
                      fields.years.value,
                      fields.months.value
                    )
                  }
                  isDisabled={false}
                  type={Button.Types.Destructive}
                  isInline={true}
                >
                  {s.removeAddress()}
                </Button>
              </InlineChildren>
            )}
          </StackChildren>
        </form>
      )}
    </Form>
  )
}
